import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Search, Edit, Save, X, RefreshCw, AlertTriangle, Shield, Trash2, Eye, FileText } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  plan_type: string;
  credits_remaining: number;
  monthly_limit: number;
  image_generations_used: number;
  created_at: string;
  updated_at: string;
  is_placeholder?: boolean;
}

// Updated plan configurations to match the new pricing model
const PLAN_CONFIGS = {
  free: { 
    monthly_limit: 250, 
    credits_per_month: 250,
    display_name: 'Free Forever'
  },
  hobby: { 
    monthly_limit: 5000, 
    credits_per_month: 5000,
    display_name: 'Hobby'
  },
  creator: { 
    monthly_limit: 16000, 
    credits_per_month: 16000,
    display_name: 'Creator'
  },
  professional: { 
    monthly_limit: 50000, 
    credits_per_month: 50000,
    display_name: 'Professional'
  },
  business: { 
    monthly_limit: 150000, 
    credits_per_month: 150000,
    display_name: 'Business'
  },
  pay_as_go: { 
    monthly_limit: 999999, 
    credits_per_month: 0,
    display_name: 'Pay as You Go'
  }
};

const AdminUserManagement = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<UserProfile>>({});
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [auditResults, setAuditResults] = useState<any>(null);
  const [showAudit, setShowAudit] = useState(false);
  const [unblockingEmail, setUnblockingEmail] = useState('');
  const [showUnblockDialog, setShowUnblockDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [allowedEmails, setAllowedEmails] = useState<Set<string>>(new Set());
  const USERS_PER_PAGE = 10;

  // Enhanced admin action logging
  const logAdminAction = async (actionType: string, targetData: any = {}) => {
    try {
      const { error } = await supabase.rpc('log_admin_action', {
        action_type: actionType,
        target_data: targetData
      });
      
      if (error) {
        console.warn('Failed to log admin action:', error);
      }
    } catch (err) {
      console.warn('Error logging admin action:', err);
    }
  };

  const fetchUsers = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching user profiles for page:', page);
      
      // Fetch allowed emails list for comparison
      const { data: allowedEmailsData } = await supabase
        .from('allowed_emails')
        .select('email');
      
      let allowedSet: Set<string> = new Set();
      if (allowedEmailsData) {
        allowedSet = new Set(
          allowedEmailsData
            .map((item: any) => (item.email || '').toLowerCase())
            .filter((e: string) => !!e)
        );
        setAllowedEmails(allowedSet);
      }
      
      // Use secure function instead of view to protect user data
      const { data, error } = await supabase.rpc('get_admin_users_secure');

      if (error) {
        console.error('Supabase error:', error);
        // Check if it's a permission error due to new security policies
        if (error.message?.includes('permission denied') || error.message?.includes('Admin privileges required')) {
          throw new Error('Access restricted: Enhanced privacy protection is now active. Only authorized admin users can access user data.');
        }
        throw error;
      }
      
      // Combine profiles with allowed emails that haven't signed up yet
      const sourceData = data || [];
      const lowercasedExisting = new Set(sourceData.map((u: any) => (u.email || '').toLowerCase()));
      const placeholders: UserProfile[] = Array.from(allowedSet)
        .filter((email) => !lowercasedExisting.has(email))
        .map((email) => ({
          id: `allowed_${email}`,
          email: email as string,
          full_name: '',
          plan_type: 'free',
          credits_remaining: 0,
          monthly_limit: 0,
          image_generations_used: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_placeholder: true,
        }));
      const combined = [...sourceData, ...placeholders];
      
      // Apply pagination on client side
      const totalUsers = combined.length;
      const startIndex = (page - 1) * USERS_PER_PAGE;
      const endIndex = startIndex + USERS_PER_PAGE;
      const paginatedData = combined.slice(startIndex, endIndex);
      
      console.log('Fetched users (combined with allowed):', paginatedData);
      setUsers(paginatedData);
      setTotalUsers(totalUsers);
      setCurrentPage(page);
      
      // Log successful data access with enhanced tracking
      await logAdminAction('user_data_access_secure', { 
        user_count: paginatedData.length || 0,
        page: page,
        total_users: totalUsers,
        access_method: 'get_admin_users_secure'
      });
      
    } catch (error: any) {
      console.error('Error fetching users:', error);
      setError(error.message || 'Failed to fetch users');
      
      // Provide helpful error messages for security-related failures
      if (error.message?.includes('Enhanced privacy protection')) {
        toast.error('🔒 ' + error.message);
      } else {
        toast.error('Error loading users: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to unblock permanently deleted users
  const unblockUser = async (email: string) => {
    try {
      setLoading(true);
      console.log('🔓 Unblocking permanently deleted user:', email);
      
      const { data, error } = await supabase.rpc('unblock_permanently_deleted_user', {
        user_email: email,
        admin_reason: 'Admin override - allowing user to re-register'
      });
      
      if (error) {
        console.error('❌ Error unblocking user:', error);
        throw error;
      }
      
      console.log('✅ User unblocked successfully');
      toast.success(`✅ User ${email} has been unblocked and can now register again`);
      
      // Log the admin action
      await logAdminAction('user_unblocked', {
        unblocked_email: email,
        reason: 'admin_override'
      });
      
    } catch (error: any) {
      console.error('🚨 Error unblocking user:', error);
      toast.error(`❌ Error unblocking user: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
      setShowUnblockDialog(false);
      setUnblockingEmail('');
    }
  };

  // Run security audit
  const runSecurityAudit = async () => {
    try {
      setLoading(true);
      console.log('Running security audit...');
      
      const { data, error } = await supabase.rpc('create_security_audit');
      
      if (error) {
        console.error('Security audit error:', error);
        throw error;
      }
      
      setAuditResults(data);
      setShowAudit(true);
      
      console.log('Security audit completed:', data);
      toast.success('Security audit completed successfully');
      
    } catch (error: any) {
      console.error('Error running security audit:', error);
      toast.error('Error running security audit: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Initialize data fetching
  useEffect(() => {
    fetchUsers(1);
  }, []);

  const deleteUserCompletely = async (userId: string, userEmail: string) => {
    try {
      setDeleting(userId);
      console.log('🚨 Starting ENHANCED PERMANENT deletion process for user:', userId, userEmail);
      
      // Log the deletion attempt with enhanced tracking
      await logAdminAction('user_permanent_deletion_enhanced_attempt', {
        target_user_id: userId,
        target_user_email: userEmail,
        deletion_method: 'enhanced_complete_removal'
      });
      
      // Use the enhanced database function for complete deletion
      console.log('🗑️ Calling ENHANCED database function for complete user removal...');
      const { data: deletionResult, error: functionError } = await supabase.rpc(
        'delete_user_completely_enhanced', 
        { 
          user_id: userId, 
          user_email: userEmail 
        }
      );
      
      if (functionError) {
        console.error('❌ Enhanced deletion function error:', functionError);
        throw new Error(`Failed to delete user permanently: ${functionError.message}`);
      }
      
      if (!deletionResult) {
        throw new Error('Enhanced user deletion function returned false - check database logs');
      }
      
      console.log('✅ Enhanced database function completed successfully');
      
      // Update local state immediately
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
      
      // Log successful deletion with enhanced details
      await logAdminAction('user_permanent_deletion_enhanced_success', {
        deleted_user_id: userId,
        deleted_user_email: userEmail,
        deletion_method: 'enhanced_complete_removal',
        auth_deletion: 'completed',
        storage_cleanup: 'completed',
        database_cleanup: 'completed'
      });
      
      // Show comprehensive success message
      console.log('🎉 === USER PERMANENTLY DELETED & BLOCKED ===');
      toast.success(
        '✅ User completely removed from system and permanently blocked from re-registration',
        { duration: 10000 }
      );
      
    } catch (error: any) {
      console.error('🚨 CRITICAL ERROR during enhanced user deletion:', error);
      
      // Log deletion failure with enhanced error tracking
      await logAdminAction('user_permanent_deletion_enhanced_failure', {
        target_user_id: userId,
        target_user_email: userEmail,
        error_message: error.message,
        deletion_method: 'enhanced_complete_removal'
      });
      
      toast.error('❌ Error during enhanced user deletion: ' + (error.message || 'Unknown error'));
      
      // Refresh to show current state
      await fetchUsers(currentPage);
    } finally {
      setDeleting(null);
    }
  };

  const updateUser = async (userId: string, updates: Partial<UserProfile>) => {
    try {
      setUpdating(userId);
      console.log('Updating user:', userId, 'with updates:', updates);
      
      const currentUser = users.find(u => u.id === userId);
      if (!currentUser) {
        toast.error('User not found');
        return;
      }
      
      // Log the update attempt
      await logAdminAction('user_profile_update_attempt', {
        target_user_id: userId,
        target_user_email: currentUser.email,
        update_fields: Object.keys(updates)
      });
      
      // Prepare clean updates
      const cleanUpdates: any = {
        updated_at: new Date().toISOString()
      };
      
      // Handle full name
      if (updates.full_name !== undefined && updates.full_name !== currentUser.full_name) {
        cleanUpdates.full_name = updates.full_name.trim();
      }
      
      // Handle plan type changes with enhanced validation
      if (updates.plan_type !== undefined && updates.plan_type !== currentUser.plan_type) {
        cleanUpdates.plan_type = updates.plan_type;
        
        // Apply plan defaults based on new pricing model
        const planDefaults = PLAN_CONFIGS[updates.plan_type as keyof typeof PLAN_CONFIGS];
        if (planDefaults) {
          cleanUpdates.credits_remaining = planDefaults.credits_per_month;
          cleanUpdates.monthly_limit = planDefaults.monthly_limit;
          
          // Also validate with plan configurations table
          const { data: planConfig } = await supabase
            .rpc('get_plan_config', { plan_name: updates.plan_type });
          
          if (planConfig && typeof planConfig === 'object') {
            const config = planConfig as any;
            cleanUpdates.credits_remaining = config.credits_per_month || planDefaults.credits_per_month;
            cleanUpdates.monthly_limit = config.monthly_limit || planDefaults.monthly_limit;
          }
        }
      }
      
      // Handle manual credit/limit editing (allow for all plans now)
      if (updates.credits_remaining !== undefined && updates.credits_remaining !== currentUser.credits_remaining) {
        cleanUpdates.credits_remaining = Math.max(0, Number(updates.credits_remaining));
      }
      if (updates.monthly_limit !== undefined && updates.monthly_limit !== currentUser.monthly_limit) {
        cleanUpdates.monthly_limit = Math.max(1, Number(updates.monthly_limit));
      }
      
      // Only update if there are changes
      if (Object.keys(cleanUpdates).length <= 1) { // Only updated_at
        toast.info('No changes to save');
        setEditingUser(null);
        setEditForm({});
        return;
      }

      console.log('Clean updates to apply:', cleanUpdates);

      // Direct update without expecting return data to avoid 406 error
      const { error: profileError } = await supabase
        .from('profiles')
        .update(cleanUpdates)
        .eq('id', userId);

      if (profileError) {
        console.error('Profile update error:', profileError);
        throw profileError;
      }

      // Update local state immediately to avoid waiting for refresh
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, ...cleanUpdates } : user
        )
      );

      // Log successful update
      await logAdminAction('user_profile_update_success', {
        target_user_id: userId,
        target_user_email: currentUser.email,
        applied_updates: cleanUpdates
      });

      console.log('Update successful');
      toast.success('User updated successfully');
      setEditingUser(null);
      setEditForm({});
      
    } catch (error: any) {
      console.error('Error updating user:', error);
      
      // Log update failure
      await logAdminAction('user_profile_update_failure', {
        target_user_id: userId,
        error_message: error.message
      });
      
      toast.error(`Error updating user: ${error.message || 'Unknown error'}`);
      // Refresh data on error to ensure consistency
      await fetchUsers(currentPage);
    } finally {
      setUpdating(null);
    }
  };

  const startEditing = (user: UserProfile) => {
    console.log('Starting edit for user:', user);
    setEditingUser(user.id);
    setEditForm({
      full_name: user.full_name || '',
      plan_type: user.plan_type || 'free',
      credits_remaining: user.credits_remaining || 0,
      monthly_limit: user.monthly_limit || 10
    });
  };

  const cancelEditing = () => {
    setEditingUser(null);
    setEditForm({});
  };

  const saveEditing = async () => {
    if (editingUser && Object.keys(editForm).length > 0) {
      console.log('Saving edit form:', editForm);
      await updateUser(editingUser, editForm);
    }
  };

  const handleFormChange = (field: keyof UserProfile, value: string | number | boolean) => {
    console.log(`Updating form field ${field} to:`, value);
    setEditForm(prev => {
      const newForm = {
        ...prev,
        [field]: value
      };
      
      // If plan type changes, auto-update credits and monthly limit based on new pricing
      if (field === 'plan_type') {
        const planDefaults = PLAN_CONFIGS[value as keyof typeof PLAN_CONFIGS];
        if (planDefaults) {
          newForm.credits_remaining = planDefaults.credits_per_month;
          newForm.monthly_limit = planDefaults.monthly_limit;
        }
      }
      
      console.log('New form state:', newForm);
      return newForm;
    });
  };

  const resetUserCredits = async (userId: string) => {
    try {
      setUpdating(userId);
      console.log('Resetting credits for user:', userId);
      const user = users.find(u => u.id === userId);
      if (!user) {
        toast.error('User not found');
        return;
      }
      
      // Log credit reset attempt
      await logAdminAction('user_credits_reset_attempt', {
        target_user_id: userId,
        target_user_email: user.email,
        old_credits: user.credits_remaining,
        old_usage: user.image_generations_used
      });
      
      const planDefaults = PLAN_CONFIGS[user.plan_type as keyof typeof PLAN_CONFIGS];
      if (!planDefaults) {
        toast.error('Invalid plan type');
        return;
      }
      
      const newCredits = planDefaults.credits_per_month;
      const newMonthlyLimit = planDefaults.monthly_limit;
      
      const { error } = await supabase
        .from('profiles')
        .update({ 
          credits_remaining: newCredits,
          monthly_limit: newMonthlyLimit,
          image_generations_used: 0,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('Error resetting credits:', error);
        throw error;
      }
      
      // Update local state immediately
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === userId ? { 
            ...u, 
            credits_remaining: newCredits,
            monthly_limit: newMonthlyLimit,
            image_generations_used: 0
          } : u
        )
      );
      
      // Log successful credit reset
      await logAdminAction('user_credits_reset_success', {
        target_user_id: userId,
        target_user_email: user.email,
        new_credits: newCredits,
        new_monthly_limit: newMonthlyLimit
      });
      
      console.log('Credits reset successfully');
      toast.success('User credits and usage reset successfully');
      
    } catch (error: any) {
      console.error('Error resetting credits:', error);
      
      // Log credit reset failure
      await logAdminAction('user_credits_reset_failure', {
        target_user_id: userId,
        error_message: error.message
      });
      
      toast.error('Error resetting credits: ' + (error.message || 'Unknown error'));
    } finally {
      setUpdating(null);
    }
  };

  const resetMonthlyUsage = async () => {
    try {
      console.log('Resetting monthly usage for all users...');
      setLoading(true);
      
      // Log bulk reset attempt
      await logAdminAction('bulk_monthly_usage_reset_attempt', {
        total_users: users.length
      });
      
      const { data: allUsers, error: fetchError } = await supabase
        .from('profiles')
        .select('*');

      if (fetchError) {
        throw fetchError;
      }

      if (!allUsers || allUsers.length === 0) {
        toast.info('No users found to reset');
        return;
      }
      
      const updatePromises = allUsers.map(async (user) => {
        const planDefaults = PLAN_CONFIGS[user.plan_type as keyof typeof PLAN_CONFIGS];
        const newCredits = user.plan_type !== 'enterprise' && planDefaults ? planDefaults.credits_per_month : user.credits_remaining;
        const newMonthlyLimit = user.plan_type !== 'enterprise' && planDefaults ? planDefaults.monthly_limit : user.monthly_limit;
        
        return supabase
          .from('profiles')
          .update({
            image_generations_used: 0,
            credits_remaining: newCredits,
            monthly_limit: newMonthlyLimit,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
      });

      const results = await Promise.all(updatePromises);
      
      const errors = results.filter(result => result.error);
      if (errors.length > 0) {
        console.error('Some updates failed:', errors);
        toast.error(`${errors.length} users could not be updated`);
        
        // Log partial failure
        await logAdminAction('bulk_monthly_usage_reset_partial_failure', {
          total_users: allUsers.length,
          failed_count: errors.length
        });
      } else {
        toast.success(`Monthly usage reset completed for ${allUsers.length} users`);
        
        // Log successful bulk reset
        await logAdminAction('bulk_monthly_usage_reset_success', {
          total_users: allUsers.length
        });
      }
      
      await fetchUsers(currentPage);
    } catch (error: any) {
      console.error('Error resetting monthly usage:', error);
      
      // Log bulk reset failure
      await logAdminAction('bulk_monthly_usage_reset_failure', {
        error_message: error.message
      });
      
      toast.error('Error resetting monthly usage: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    // Handle cases where searchTerm is empty (show all users)
    if (!searchTerm || searchTerm.trim() === '') {
      return true;
    }
    
    const search = searchTerm.toLowerCase().trim();
    const email = user.email?.toLowerCase() || '';
    const fullName = user.full_name?.toLowerCase() || '';
    const userId = user.id?.toLowerCase() || '';
    
    return email.includes(search) || fullName.includes(search) || userId.includes(search);
  });

  // Debug logging
  console.log('Total users:', users.length);
  console.log('Search term:', searchTerm);
  console.log('Filtered users:', filteredUsers.length);
  console.log('First user sample:', users[0]);

  const totalPages = Math.ceil(totalUsers / USERS_PER_PAGE);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      fetchUsers(page);
    }
  };

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'professional': return 'bg-purple-600/20 text-purple-300 border-purple-600/30';
      case 'business': return 'bg-yellow-600/20 text-yellow-300 border-yellow-600/30';
      case 'enterprise': return 'bg-blue-600/20 text-blue-300 border-blue-600/30';
      case 'starter': return 'bg-green-600/20 text-green-300 border-green-600/30';
      case 'pay_as_go': return 'bg-orange-600/20 text-orange-300 border-orange-600/30';
      default: return 'bg-gray-600/20 text-gray-300 border-gray-600/30';
    }
  };

  const getPlanDisplayName = (planType: string) => {
    const config = PLAN_CONFIGS[planType as keyof typeof PLAN_CONFIGS];
    return config?.display_name || planType;
  };

  if (loading && users.length === 0) {
    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Users className="w-8 h-8 mx-auto mb-4 animate-pulse text-blue-400" />
            <p className="text-white">Loading users...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error && users.length === 0) {
    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardContent className="p-6">
          <div className="text-center py-8">
            <AlertTriangle className="w-8 h-8 mx-auto mb-4 text-red-400" />
            <p className="text-red-300 mb-4">Error loading users: {error}</p>
            <Button onClick={() => fetchUsers(1)} className="bg-blue-600 hover:bg-blue-700">
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <CardTitle className="text-white flex items-center">
              <Users className="w-6 h-6 mr-2" />
              User Management ({totalUsers} total users, page {currentPage} of {totalPages})
            </CardTitle>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button
                onClick={runSecurityAudit}
                className="bg-red-600 hover:bg-red-700 text-sm"
                disabled={loading}
              >
                <Shield className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Security Audit
              </Button>
              <Button
                onClick={() => setShowUnblockDialog(true)}
                className="bg-yellow-600 hover:bg-yellow-700 text-sm"
                disabled={loading}
              >
                Unblock User
              </Button>
              <Button
                onClick={resetMonthlyUsage}
                className="bg-orange-600 hover:bg-orange-700 text-sm"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Reset Monthly Usage
              </Button>
              <Button
                onClick={() => fetchUsers(currentPage)}
                className="bg-blue-600 hover:bg-blue-700 text-sm"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Manual Refresh
              </Button>
            </div>
          </div>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search users by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Pagination Controls - Top */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-gray-400">
                Showing {((currentPage - 1) * USERS_PER_PAGE) + 1} to {Math.min(currentPage * USERS_PER_PAGE, totalUsers)} of {totalUsers} users
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                >
                  Previous
                </Button>
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                    return (
                      <Button
                        key={page}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        disabled={loading}
                        className={page === currentPage 
                          ? "bg-blue-600 hover:bg-blue-700 text-white" 
                          : "bg-white/10 hover:bg-white/20 text-white border-white/20"
                        }
                      >
                        {page}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
          
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-gray-300">User</TableHead>
                  <TableHead className="text-gray-300">Plan</TableHead>
                  <TableHead className="text-gray-300">Credits</TableHead>
                  <TableHead className="text-gray-300">Monthly Limit</TableHead>
                  <TableHead className="text-gray-300">Used This Month</TableHead>
                  <TableHead className="text-gray-300">Joined</TableHead>
                  <TableHead className="text-gray-300">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <p className="text-gray-400">
                        {loading ? 'Loading users...' : 'No users found'}
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => {
                    console.log('Rendering user:', user.email, user.id);
                    return (
                      <TableRow key={user.id} className="border-white/10">
                        <TableCell>
                          <div className="min-w-[200px]">
                            {editingUser === user.id ? (
                              <Input
                                value={editForm.full_name || ''}
                                onChange={(e) => handleFormChange('full_name', e.target.value)}
                                className="bg-white/10 border-white/20 text-white text-sm mb-1"
                                placeholder="Full name"
                              />
                            ) : (
                          <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="text-white font-medium text-sm">{user.full_name || 'No name'}</p>
                                  {allowedEmails.has(user.email.toLowerCase()) && (
                                    <Badge className="bg-blue-600/20 text-blue-300 border-blue-600/30 text-xs">
                                      Allowed List
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-gray-400 text-xs break-all">{user.email}</p>
                                <p className="text-gray-500 text-xs">ID: {user.id.slice(0, 8)}...</p>
                              </div>
                            )}
                          </div>
                        </TableCell>
                    <TableCell>
                      {editingUser === user.id ? (
                        <Select 
                          value={editForm.plan_type || user.plan_type} 
                          onValueChange={(value) => handleFormChange('plan_type', value)}
                        >
                          <SelectTrigger className="w-32 bg-white/10 border-white/20 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700">
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="starter">Starter</SelectItem>
                            <SelectItem value="professional">Professional</SelectItem>
                            <SelectItem value="business">Business</SelectItem>
                            <SelectItem value="pay_as_go">Pay as You Go</SelectItem>
                            <SelectItem value="enterprise">Enterprise</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge className={getPlanBadgeColor(user.plan_type)}>
                          {getPlanDisplayName(user.plan_type)}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingUser === user.id ? (
                        <Input
                          type="number"
                          value={editForm.credits_remaining ?? user.credits_remaining}
                          onChange={(e) => handleFormChange('credits_remaining', parseInt(e.target.value) || 0)}
                          className="w-20 bg-white/10 border-white/20 text-white"
                          min="0"
                        />
                      ) : (
                        <span className="text-white">{user.credits_remaining || 0}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingUser === user.id ? (
                        <Input
                          type="number"
                          value={editForm.monthly_limit ?? user.monthly_limit}
                          onChange={(e) => handleFormChange('monthly_limit', parseInt(e.target.value) || 0)}
                          className="w-20 bg-white/10 border-white/20 text-white"
                          min="1"
                        />
                      ) : (
                        <span className="text-white">
                          {user.monthly_limit === 999999 ? '∞' : user.monthly_limit || 0}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-white">{user.image_generations_used || 0}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-white">
                        {new Date(user.created_at).toLocaleDateString()}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {editingUser === user.id ? (
                          <>
                            <Button
                              size="sm"
                              onClick={saveEditing}
                              className="bg-green-600 hover:bg-green-700"
                              disabled={updating === user.id}
                            >
                              <Save className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={cancelEditing}
                              className="bg-gray-600 hover:bg-gray-700"
                              disabled={updating === user.id}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              onClick={() => startEditing(user)}
                              className="bg-blue-600 hover:bg-blue-700 mb-1"
                              disabled={user.is_placeholder || updating === user.id || deleting === user.id}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => resetUserCredits(user.id)}
                              className="bg-purple-600 hover:bg-purple-700 mb-1"
                              disabled={user.is_placeholder || updating === user.id || deleting === user.id}
                            >
                              {updating === user.id ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                'Reset Credits'
                              )}
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="sm"
                                  className="bg-red-600 hover:bg-red-700"
                                  disabled={user.is_placeholder || updating === user.id || deleting === user.id}
                                >
                                  {deleting === user.id ? (
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-3 h-3" />
                                  )}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-slate-800 border-slate-700">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-white">
                                    ⚠️ PERMANENTLY DELETE USER
                                  </AlertDialogTitle>
                                  <AlertDialogDescription className="text-gray-300">
                                    <div className="space-y-4">
                                      <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-4 text-center">
                                        <div className="text-red-300 font-bold mb-2">
                                          🚨 PERMANENT DELETION WARNING 🚨
                                        </div>
                                        <div className="text-white">
                                          You are about to PERMANENTLY DELETE:
                                        </div>
                                      </div>
                                      
                                      <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                                        <div className="text-white font-medium">{user.full_name || 'No name'}</div>
                                        <div className="text-gray-400 text-sm">{user.email}</div>
                                      </div>

                                      <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-3">
                                        <div className="text-red-300 font-medium text-sm mb-2">This will PERMANENTLY:</div>
                                        <div className="text-xs space-y-1 text-red-200">
                                          <div>• Delete ALL user data from database</div>
                                          <div>• Remove ALL generated images and files</div>
                                          <div>• Delete notifications, history, and settings</div>
                                          <div>• Remove admin privileges and permissions</div>
                                          <div>• BLOCK user from ever re-registering</div>
                                          <div>• Delete from authentication system</div>
                                        </div>
                                      </div>

                                      <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-3 text-center">
                                        <div className="text-yellow-300 text-sm font-bold">
                                          ⚠️ THIS ACTION CANNOT BE UNDONE ⚠️
                                        </div>
                                        <div className="text-yellow-300 text-xs mt-1">
                                          User will be permanently blocked from creating new accounts.
                                        </div>
                                      </div>
                                    </div>
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="bg-gray-600 hover:bg-gray-700 text-white">
                                    Cancel
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteUserCompletely(user.id, user.email)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    🗑️ PERMANENTLY DELETE USER
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </>
                        )}
                      </div>
                    </TableCell>
                   </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination Controls - Bottom */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-gray-400">
                Showing {((currentPage - 1) * USERS_PER_PAGE) + 1} to {Math.min(currentPage * USERS_PER_PAGE, totalUsers)} of {totalUsers} users
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                >
                  Previous
                </Button>
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                    return (
                      <Button
                        key={page}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        disabled={loading}
                        className={page === currentPage 
                          ? "bg-blue-600 hover:bg-blue-700 text-white" 
                          : "bg-white/10 hover:bg-white/20 text-white border-white/20"
                        }
                      >
                        {page}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
          
          {filteredUsers.length === 0 && !loading && (
            <div className="text-center py-8">
              <p className="text-gray-400">No users found matching your search.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security Audit Results Modal */}
      {showAudit && auditResults && (
        <Card className="bg-white/10 backdrop-blur-md border-white/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <FileText className="w-6 h-6 mr-2" />
              Security Audit Results
              <Button
                onClick={() => setShowAudit(false)}
                className="ml-auto bg-gray-600 hover:bg-gray-700"
                size="sm"
              >
                <X className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-600/20 p-3 rounded-lg">
                  <div className="text-blue-300 text-sm">Total Admins</div>
                  <div className="text-white text-xl font-bold">{auditResults.admin_count}</div>
                </div>
                <div className="bg-green-600/20 p-3 rounded-lg">
                  <div className="text-green-300 text-sm">Total Users</div>
                  <div className="text-white text-xl font-bold">{auditResults.total_users}</div>
                </div>
                <div className="bg-yellow-600/20 p-3 rounded-lg">
                  <div className="text-yellow-300 text-sm">Security Events (24h)</div>
                  <div className="text-white text-xl font-bold">{auditResults.recent_security_events}</div>
                </div>
                <div className="bg-purple-600/20 p-3 rounded-lg">
                  <div className="text-purple-300 text-sm">RLS Tables</div>
                  <div className="text-white text-xl font-bold">
                    {auditResults.rls_enabled_tables?.length || 0}
                  </div>
                </div>
              </div>
              
              {auditResults.anomaly_detection?.alerts?.length > 0 && (
                <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
                  <div className="text-red-300 font-medium mb-2">🚨 Security Alerts</div>
                  {auditResults.anomaly_detection.alerts.map((alert: any, index: number) => (
                    <div key={index} className="text-red-200 text-sm mb-1">
                      • {alert.message} ({alert.count} occurrences)
                    </div>
                  ))}
                </div>
              )}
              
              <div className="text-gray-400 text-sm">
                Audit conducted on: {new Date(auditResults.audit_timestamp).toLocaleString()}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Unblock User Dialog */}
      <AlertDialog open={showUnblockDialog} onOpenChange={setShowUnblockDialog}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              🔓 Unblock Permanently Deleted User
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              <div className="space-y-4">
                <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-4">
                  <div className="text-yellow-300 font-bold mb-2">
                    ⚠️ ADMIN ACTION REQUIRED
                  </div>
                  <div className="text-white text-sm">
                    Enter the email address of a permanently deleted user to unblock them from re-registration.
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-white text-sm font-medium">Email Address:</label>
                  <Input
                    value={unblockingEmail}
                    onChange={(e) => setUnblockingEmail(e.target.value)}
                    placeholder="Enter email address to unblock"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>

                <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-3">
                  <div className="text-blue-300 text-sm">
                    This will remove the permanent deletion record and allow the user to register again with this email address.
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-600 hover:bg-gray-700 text-white">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => unblockUser(unblockingEmail)}
              disabled={!unblockingEmail.trim() || loading}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              🔓 Unblock User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminUserManagement;
